import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { shouldProbeStaffSession } from '../Admin/staffSessionGate';
import { getStaffSession, readIsStaff } from '../services/staffService';
import { useAuth } from './AuthContext';
import { savePostLoginRedirect } from '../Utils/postLoginRedirect';

const StaffSessionContext = createContext(null);

/**
 * `is_staff` solo en memoria. El GET corre al entrar a `/admin`, no en el producto.
 */
export function StaffSessionProvider({ children }) {
  const { isLoggedIn, isLoading: authLoading, user, logout: authLogout } = useAuth();
  const { pathname } = useLocation();
  const [isStaff, setIsStaff] = useState(false);
  const [probeStatus, setProbeStatus] = useState('idle');

  const currentUserId = typeof user?.sub === 'string' ? user.sub : '';
  const probing = shouldProbeStaffSession(pathname, isLoggedIn);

  const refreshStaffSession = useCallback(async (signal) => {
    if (!isLoggedIn) {
      setIsStaff(false);
      setProbeStatus('ready');
      return { isStaff: false, status: null };
    }

    setProbeStatus('loading');
    try {
      const response = await getStaffSession({ signal });
      if (signal?.aborted) return { isStaff: false, status: response._httpStatus };
      if (response._httpStatus === 401) {
        setIsStaff(false);
        setProbeStatus('ready');
        return { isStaff: false, status: 401 };
      }
      const flag = readIsStaff(response);
      setIsStaff(flag);
      setProbeStatus('ready');
      return { isStaff: flag, status: response._httpStatus };
    } catch (error) {
      if (error?.name === 'AbortError') return { isStaff: false, status: null };
      console.error('Staff session: GET /getstaffsession falló', error);
      setIsStaff(false);
      setProbeStatus('ready');
      return { isStaff: false, status: null };
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (authLoading) return undefined;
    if (!probing) {
      if (!isLoggedIn) {
        setIsStaff(false);
        setProbeStatus('idle');
      }
      return undefined;
    }

    const controller = new AbortController();
    refreshStaffSession(controller.signal);
    return () => controller.abort();
  }, [authLoading, probing, refreshStaffSession, isLoggedIn]);

  useEffect(() => {
    if (!probing) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        refreshStaffSession();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [probing, refreshStaffSession]);

  const logout = useCallback(async () => {
    setIsStaff(false);
    setProbeStatus('idle');
    await authLogout();
  }, [authLogout]);

  const isLoading = authLoading || (probing && probeStatus !== 'ready');

  const value = useMemo(
    () => ({
      isStaff,
      isLoading,
      currentUserId,
      refreshStaffSession,
      logout,
    }),
    [isStaff, isLoading, currentUserId, refreshStaffSession, logout],
  );

  return (
    <StaffSessionContext.Provider value={value}>
      {children}
    </StaffSessionContext.Provider>
  );
}

export function useStaffSession() {
  const context = useContext(StaffSessionContext);
  if (!context) {
    throw new Error('useStaffSession must be used within a StaffSessionProvider');
  }
  return context;
}

/**
 * Interpreta 401/403 de un ABM: 401 → login; 403 → revalidar gate.
 * Un 403 de regla de negocio (último staff, último owner) no saca del panel
 * si `getstaffsession` sigue diciendo `is_staff`.
 *
 * @returns {'ok'|'unauthorized'|'forbidden'|'kicked'}
 */
export function useAdminHttpGuard() {
  const { refreshStaffSession, logout } = useStaffSession();
  const navigate = useNavigate();

  return useCallback(async (response) => {
    const status = response?._httpStatus;
    if (status === 401) {
      await logout();
      savePostLoginRedirect('/admin');
      navigate('/login', { replace: true });
      return 'unauthorized';
    }
    if (status === 403) {
      const next = await refreshStaffSession();
      if (!next.isStaff) return 'kicked';
      return 'forbidden';
    }
    return 'ok';
  }, [logout, navigate, refreshStaffSession]);
}
